/**
 * Set to `1` by the Enhanced UI style mixins (border tint, fill spot, wash) on
 * every element they style, and registered as non-inheriting. The tracker
 * treats exactly those elements as targets, so styling an element is all it
 * takes -- there is no second list of selectors to keep in step.
 */
export const GLOW_TARGET_PROPERTY = '--if-glow-target';

/** Pointer position within the element's border box, read by the glow CSS. */
export const GLOW_X_PROPERTY = '--if-glow-x';
export const GLOW_Y_PROPERTY = '--if-glow-y';
/** Radius of the glow, from the element's size (see `glowRadius`). */
export const GLOW_R_PROPERTY = '--if-glow-r';

const MIN_RADIUS = 24;
const MAX_RADIUS = 120;

/**
 * Glow radius for an element: half its longer side, kept between 24px and
 * 120px. The light then reaches about half-way across any control, so a chip,
 * a button, a full-width row and a card read alike. Below 24px the ring would
 * be a dot; above 120px the falloff is so gentle that a wide row or card
 * would look evenly outlined rather than lit near the pointer.
 */
export function glowRadius(width: number, height: number): number {
  return Math.round(Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, 0.5 * Math.max(width, height))));
}

const DISABLED_SELECTOR = ':disabled, [aria-disabled="true"]';

export interface FrameScheduler {
  requestFrame: (callback: FrameRequestCallback) => number;
  cancelFrame: (id: number) => void;
}

/**
 * Writes the pointer position onto the glow elements under the pointer, at
 * most once per animation frame.
 *
 * Two passive document listeners, and nothing else:
 * - `pointerover` and `pointermove` work out which glow elements are under the
 *   pointer -- those whose styles set GLOW_TARGET_PROPERTY -- from the composed
 *   path, so controls inside open shadow roots are found too. The path is only
 *   re-read when the innermost element under the pointer changes. `pointermove`
 *   covers what `pointerover` cannot: when the pointer moves between two
 *   elements inside the same shadow root, `pointerover` never leaves that root,
 *   so the document does not see it;
 * - `pointermove` also stores the coordinates and asks for one frame.
 * The frame reads each element's box and sets two custom properties (plus
 * the radius, when the element's size gives a new one). A still pointer
 * schedules nothing. The fade in and out is CSS (`:hover`).
 *
 * Framework-free so it can be registered outside Angular's zone.
 */
export class PointerGlowTracker {
  private targets: readonly HTMLElement[] = [];
  /** Innermost element of the last composed path read; the path is re-read when it changes. */
  private lastNode: EventTarget | null = null;
  private x = 0;
  private y = 0;
  private frame: number | null = null;
  private listening = false;
  private readonly scheduler: FrameScheduler;
  /** Last radius written per element; it only changes when the element resizes. */
  private radii = new WeakMap<HTMLElement, number>();

  constructor(
    private readonly doc: Document,
    scheduler?: FrameScheduler
  ) {
    const view = doc.defaultView;
    this.scheduler = scheduler ?? {
      requestFrame: (callback) => (view ? view.requestAnimationFrame(callback) : 0),
      cancelFrame: (id) => view?.cancelAnimationFrame(id),
    };
  }

  get isListening(): boolean {
    return this.listening;
  }

  start(): void {
    if (this.listening) return;
    this.listening = true;
    this.doc.addEventListener('pointerover', this.onPointerOver, { capture: true, passive: true });
    this.doc.addEventListener('pointermove', this.onPointerMove, { capture: true, passive: true });
  }

  /** Removes the listeners, drops a pending frame and clears the properties it wrote. */
  stop(): void {
    if (!this.listening) return;
    this.listening = false;
    this.doc.removeEventListener('pointerover', this.onPointerOver, { capture: true });
    this.doc.removeEventListener('pointermove', this.onPointerMove, { capture: true });
    if (this.frame !== null) {
      this.scheduler.cancelFrame(this.frame);
      this.frame = null;
    }
    for (const el of this.targets) {
      el.style.removeProperty(GLOW_X_PROPERTY);
      el.style.removeProperty(GLOW_Y_PROPERTY);
      el.style.removeProperty(GLOW_R_PROPERTY);
    }
    this.targets = [];
    this.lastNode = null;
    this.radii = new WeakMap();
  }

  private readonly onPointerOver = (event: Event): void => {
    if (isTouch(event)) return;
    this.readPath(event);
    this.onPointerMove(event);
  };

  private readonly onPointerMove = (event: Event): void => {
    if (isTouch(event)) return;
    this.readPath(event);
    if (this.targets.length === 0) return;
    const { clientX, clientY } = event as MouseEvent;
    this.x = clientX;
    this.y = clientY;
    if (this.frame === null) {
      this.frame = this.scheduler.requestFrame(this.flush);
    }
  };

  private readonly flush = (): void => {
    this.frame = null;
    for (const el of this.targets) {
      const rect = el.getBoundingClientRect();
      el.style.setProperty(GLOW_X_PROPERTY, `${Math.round(this.x - rect.left)}px`);
      el.style.setProperty(GLOW_Y_PROPERTY, `${Math.round(this.y - rect.top)}px`);
      const radius = glowRadius(rect.width, rect.height);
      if (this.radii.get(el) !== radius) {
        this.radii.set(el, radius);
        el.style.setProperty(GLOW_R_PROPERTY, `${radius}px`);
      }
    }
  };

  private readPath(event: Event): void {
    const path = event.composedPath();
    const innermost = path[0] ?? null;
    if (innermost === this.lastNode) return;
    this.lastNode = innermost;
    this.targets = this.collect(path);
  }

  private collect(path: readonly EventTarget[]): HTMLElement[] {
    const view = this.doc.defaultView;
    if (!view) return [];
    const found: HTMLElement[] = [];
    for (const node of path) {
      if (!isHtmlElement(node)) continue;
      if (isGlowTarget(view, node) && !node.matches(DISABLED_SELECTOR)) {
        found.push(node);
      }
    }
    return found;
  }
}

function isGlowTarget(view: Window, el: HTMLElement): boolean {
  return view.getComputedStyle(el).getPropertyValue(GLOW_TARGET_PROPERTY).trim() === '1';
}

function isTouch(event: Event): boolean {
  return (event as PointerEvent).pointerType === 'touch';
}

function isHtmlElement(node: EventTarget): node is HTMLElement {
  return (node as Node).nodeType === 1 && typeof (node as HTMLElement).matches === 'function'
    && (node as HTMLElement).style !== undefined;
}
